import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "@phosphor-icons/react";
import MerchantLayout from "@/components/MerchantLayout";
import TablePagination from "@/components/TablePagination";
import { MERCHANT } from "@/lib/routes";
import { mApi } from "@/lib/api";

const fmt$ = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PAGE_SIZE = 25;

const fmtDelta = (v) => {
  const n = Number(v || 0);
  const abs = fmt$(Math.abs(n));
  if (n < 0) return `-${abs}`;
  if (n > 0) return `+${abs}`;
  return abs;
};

export default function MerchantWallet() {
  const [balance, setBalance] = useState(0);
  const [currency, setCurrency] = useState("USD");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await mApi.get("/merchant/balance-funding", { params: { page, limit } });
      setBalance(r.data.balance ?? 0);
      setCurrency(r.data.currency ?? "USD");
      setItems(r.data.items ?? []);
      setTotal(r.data.total ?? 0);
      setPages(r.data.pages ?? 0);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [limit]);

  return (
    <MerchantLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="m-wallet-page">
        <h1 className="font-display text-3xl tracking-tight font-medium mb-1">Wallet</h1>
        <p className="text-sm text-zinc-500 mb-6">
          Platform balance used for card issuance fees and card loads. Funded by Blonix admin.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-white border border-zinc-200 rounded-sm p-5">
            <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">Available balance</div>
            <div className="mt-2 font-display text-3xl tracking-tight tabular-nums">{fmt$(balance)}</div>
            <div className="text-xs text-zinc-500 mt-1">{currency}</div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-sm p-5 flex flex-col justify-center">
            <p className="text-sm text-zinc-600">
              If you see &quot;Insufficient merchant balance for issuance fee&quot;, contact your Blonix account manager to top up this wallet.
            </p>
            <Link to={MERCHANT.cards} className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-zinc-950 hover:underline">
              Issue cards <ArrowUpRight size={12} />
            </Link>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm ">
          <div className="px-5 py-4 border-b border-zinc-100 font-display text-lg tracking-tight font-medium">
            Funding history
            <span className="ml-2 text-sm font-normal text-zinc-500 tabular-nums">({total})</span>
          </div>
          <div className="table-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 border-b border-zinc-100">
                <th className="px-5 py-3">Date</th>
                <th className="px-5 py-3 text-right">Amount</th>
                <th className="px-5 py-3 text-right">Balance after</th>
                <th className="px-5 py-3">Note</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={4} className="px-5 py-12 text-center text-xs text-zinc-500">Loading…</td></tr>
              )}
              {!loading && items.map((f) => (
                <tr key={f.id} className="border-b border-zinc-50">
                  <td className="px-5 py-3 text-xs text-zinc-500">{(f.created_at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className={`px-5 py-3 text-right font-mono tabular-nums ${Number(f.amount) < 0 ? "text-red-700" : "text-emerald-700"}`}>
                    {fmtDelta(f.amount)}
                  </td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums">{fmt$(f.balance_after)}</td>
                  <td className="px-5 py-3 text-xs text-zinc-600">{f.note || "—"}</td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-12 text-center text-xs text-zinc-500">
                    No funding recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
          <TablePagination
            page={page}
            pages={pages}
            total={total}
            limit={limit}
            loading={loading}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </div>
      </div>
    </MerchantLayout>
  );
}
